import type { CSSProperties, ReactElement, Ref } from 'react';
import { forwardRef } from 'react';

import classnames from 'classnames';

import styles from './IconButton.module.css';

import type { ColorName } from '../../styles';
import { Color, getColor } from '../../styles';
import type { ButtonBaseProps } from '../ButtonBase';
import { ButtonBase } from '../ButtonBase';
import type { IconSvgProps } from '../IconSvg';
import type { LinkProps } from '../Link';
import { Link } from '../Link';

export interface IconButtonBase {
  /** The ARIA label of the button. */
  ['aria-label']: string;
  /** An [icon](/system/icons) to render as button. */
  icon: ReactElement<IconSvgProps>;
  /**
   * Add a solid color background to the button. Use in combination with the
   * icon's `baseColor`.
   */
  backgroundColor?: Color | ColorName;
}

export type IconButtonLink = LinkProps &
  IconButtonBase &
  ({ href: string } | { to: string });
export type IconButtonDefault = ButtonBaseProps &
  IconButtonBase & { type?: 'button' | 'submit' | 'reset' | undefined };

export type IconButtonProps = IconButtonDefault | IconButtonLink;

function isIconButtonLink(props: IconButtonProps): props is IconButtonLink {
  return (
    ('href' in props && Boolean(props.href)) ||
    ('to' in props && Boolean(props.to))
  );
}
/**
 * Display an icon inside a button or a link. Pass to this component any icon
 * from our {@link https://corgi-x.tfd.engineering/system/icons | Icon Library}.
 *
 * @example
 *
 * ```tsx
 * <IconButton
 *   icon={<Star size={16} baseColor="Charcoal3" />}
 *   backgroundColor="Carrot3"
 * />;
 * ```
 *
 * @see https://corgi-x.tfd.engineering/components/iconbutton
 */
export const IconButton = forwardRef<HTMLElement, IconButtonProps>(
  (props, ref) => {
    let style: CSSProperties = { ...props.style };

    if (isIconButtonLink(props)) {
      const { icon, backgroundColor, ...linkProps } = props;
      style = {
        ...style,
        backgroundColor: backgroundColor
          ? getColor(backgroundColor)
          : undefined,
      };

      return (
        <Link
          {...linkProps}
          className={classnames(styles.iconButton, linkProps.className)}
          style={style}
          ref={ref as Ref<HTMLAnchorElement>}
        >
          {icon}
        </Link>
      );
    }

    const { icon, backgroundColor, type: rawType, ...rest } = props;

    let type: 'button' | 'submit' | 'reset' | undefined = undefined;
    if (
      rawType === 'button' ||
      rawType === 'submit' ||
      rawType === 'reset' ||
      typeof rawType === 'undefined'
    ) {
      type = rawType;
    }

    const buttonProps = { ...rest } as Omit<ButtonBaseProps, 'type'> & {
      type?: 'button' | 'submit' | 'reset';
    };

    if (typeof type !== 'undefined') {
      buttonProps.type = type;
    }

    const isDisabled = props['aria-disabled'] === true;
    if (isDisabled) {
      style.backgroundColor = Color.Charcoal0;
    } else if (backgroundColor) {
      style.backgroundColor = getColor(backgroundColor);
    }

    return (
      <ButtonBase
        {...buttonProps}
        className={classnames(styles.iconButton, buttonProps.className)}
        style={style}
        ref={ref as Ref<HTMLButtonElement>}
      >
        {icon}
      </ButtonBase>
    );
  }
);
